/**
 * Admin Orchestrator Config — Manage prompts, timeouts, and settings
 */

import { useState, useEffect, useRef } from 'preact/hooks';
import { isAdmin } from '../stores/auth.store';
import { api } from '../api/client';
import { SkeletonBar, Button, Textarea } from '../components/ui';
import { T } from '../styles/tokens';

const FIELD_META: Record<string, { label: string; description: string; type: 'text' | 'number' | 'textarea' }> = {
  planner_system_prompt: {
    label: 'Prompt do Planejador (Hub-Spoke)',
    description: 'Prompt enviado ao LLM para decompor o pedido do usuário em tasks atômicas. Deve instruir formato JSON com tasks, dependências e estimativas.',
    type: 'textarea',
  },
  team_system_prompt: {
    label: 'Prompt do Agente (Team Mode)',
    description: 'Instruções enviadas a cada agente quando está em modo equipe. Define como responder ([TEAM_MSG]), quando finalizar ([TEAM_DONE]) e regras de colaboração.',
    type: 'textarea',
  },
  hybrid_planner_prompt: {
    label: 'Prompt do Planejador (Híbrido)',
    description: 'Prompt enviado ao LLM para decompor o pedido em GRUPOS DE TRABALHO (teams) em vez de tasks individuais. Define formato JSON com teams, objetivos e dependências.',
    type: 'textarea',
  },
  reviewer_prompt: {
    label: 'Prompt do Revisor de Qualidade',
    description: 'Prompt enviado ao LLM para revisar a qualidade do output de cada task. Avalia se o resultado cumpre o pedido.',
    type: 'textarea',
  },
  skeleton_system_prompt: {
 label: 'Prompt do Esqueleto (Chunked)',
    description: 'Prompt para gerar estrutura de fases em planos complexos. Usado na primeira chamada do chunked planning.',
    type: 'textarea',
  },
  phase_detail_prompt: {
    label: 'Prompt de Detalhe por Fase',
    description: 'Prompt para gerar tasks detalhadas de cada fase individualmente. Recebe contexto acumulado das fases anteriores.',
    type: 'textarea',
  },
  estimate_system_prompt: {
    label: 'Prompt de Estimativa de Custo',
    description: 'Prompt para análise rápida de custo e complexidade antes de criar o plano.',
    type: 'textarea',
  },
  synthesis_prompt: {
 label: 'Prompt de Síntese Final',
    description: 'Prompt para consolidar resultados de todas as tasks em um entregável final coeso.',
    type: 'textarea',
  },
  sdd_enabled: {
    label: 'SDD Habilitado',
    description: 'Ativa a metodologia Spec-Driven Development: cada task passa por Research → Spec → Execute. Se desativado, execução direta.',
    type: 'boolean',
  },
  sdd_skip_research_simple: {
    label: 'Pular Research em Tasks Simples',
    description: 'Se true, pula a etapa de Research para planos com complexidade "simple". Vai direto para Spec.',
    type: 'boolean',
  },
  sdd_research_prompt: {
    label: 'Prompt SDD: Research (Generic/Fallback)',
    description: 'Prompt genérico de Research. Usado quando não há prompt específico da categoria.',
    type: 'textarea',
  },
  sdd_spec_prompt: {
    label: 'Prompt SDD: Spec (Generic/Fallback)',
    description: 'Prompt genérico de Spec. Usado quando não há prompt específico da categoria.',
    type: 'textarea',
  },
  sdd_execute_prompt: {
    label: 'Prompt SDD: Execute (Generic/Fallback)',
    description: 'Prompt genérico de Execute. Usado quando não há prompt específico da categoria.',
    type: 'textarea',
  },
  // Category-specific SDD prompts
  sdd_programming_research_prompt: {
    label: 'Research (Programming)',
    description: 'Pesquisa técnica: codebase, padrões existentes, docs de APIs, riscos técnicos.',
    type: 'textarea',
  },
  sdd_programming_spec_prompt: {
    label: 'Spec (Programming)',
    description: 'Spec de implementação: arquivos a modificar/criar, mudanças exatas, code snippets.',
    type: 'textarea',
  },
  sdd_programming_execute_prompt: {
    label: 'Execute (Programming)',
    description: 'Implementação fiel à spec: código modular, padrões do projeto, tratamento de erros.',
    type: 'textarea',
  },
  sdd_analysis_research_prompt: {
    label: 'Research (Analysis)',
    description: 'Pesquisa de dados: métricas, benchmarks, tendências, fontes confiáveis.',
    type: 'textarea',
  },
  sdd_analysis_spec_prompt: {
    label: 'Spec (Analysis)',
    description: 'Estrutura de análise: metodologia, métricas-chave, formato do relatório.',
    type: 'textarea',
  },
  sdd_analysis_execute_prompt: {
    label: 'Execute (Analysis)',
    description: 'Relatório final: dados, insights, recomendações actionable.',
    type: 'textarea',
  },
  sdd_content_research_prompt: {
    label: 'Research (Content)',
    description: 'Pesquisa editorial: público-alvo, tom/voz, referências, keywords.',
    type: 'textarea',
  },
  sdd_content_spec_prompt: {
    label: 'Spec (Content)',
    description: 'Outline detalhado: títulos, seções, pontos-chave, CTA.',
    type: 'textarea',
  },
  sdd_content_execute_prompt: {
    label: 'Execute (Content)',
    description: 'Conteúdo final: redação profissional, formatado, pronto para publicação.',
    type: 'textarea',
  },
  sdd_generic_research_prompt: {
    label: 'Research (Generic)',
    description: 'Prompt de research genérico para tarefas não categorizadas.',
    type: 'textarea',
  },
  sdd_generic_spec_prompt: {
    label: 'Spec (Generic)',
    description: 'Prompt de spec genérico para tarefas não categorizadas.',
    type: 'textarea',
  },
  sdd_generic_execute_prompt: {
    label: 'Execute (Generic)',
    description: 'Prompt de execute genérico para tarefas não categorizadas.',
    type: 'textarea',
  },
  max_rounds: {
 label: 'Máximo de Rodadas (Team/Híbrido)',
    description: 'Número máximo de rodadas de conversa entre agentes antes de forçar encerramento. Default: 20.',
    type: 'number',
  },
  max_messages: {
    label: 'Máximo de Mensagens (Team/Híbrido)',
    description: 'Número máximo total de mensagens em um channel de equipe. Default: 100.',
    type: 'number',
  },
  timeout_min: {
 label: 'Timeout do Channel (minutos)',
    description: 'Tempo máximo em minutos que um channel de equipe pode ficar ativo. Default: 30.',
    type: 'number',
  },
  task_timeout_min: {
 label: 'Timeout de Task (minutos)',
    description: 'Tempo máximo em minutos que uma task individual pode ficar sem resposta antes de ser marcada como falha. Default: 30.',
    type: 'number',
  },
  max_retries: {
 label: 'Máximo de Retries por Task',
    description: 'Quantas vezes uma task pode ser reenviada ao agente se o resultado for reprovado na revisão. Default: 2.',
    type: 'number',
  },
  convergence_interval: {
    label: 'Intervalo de Verificação de Convergência',
    description: 'A cada N rodadas, o LLM analisa se a conversa do team está convergindo para uma solução ou em loop. Default: 5.',
    type: 'number',
  },
};

const SDD_CATEGORIES = [
  { id: 'programming', label: 'Programming', icon: '' },
  { id: 'analysis', label: 'Analysis', icon: '' },
  { id: 'content', label: 'Content', icon: '' },
  { id: 'generic', label: 'Generic', icon: '' },
];

const SDD_CATEGORY_FIELDS: Record<string, string[]> = {
  programming: ['sdd_programming_research_prompt', 'sdd_programming_spec_prompt', 'sdd_programming_execute_prompt'],
  analysis: ['sdd_analysis_research_prompt', 'sdd_analysis_spec_prompt', 'sdd_analysis_execute_prompt'],
  content: ['sdd_content_research_prompt', 'sdd_content_spec_prompt', 'sdd_content_execute_prompt'],
  generic: ['sdd_generic_research_prompt', 'sdd_generic_spec_prompt', 'sdd_generic_execute_prompt'],
};

const FIELD_ORDER = [
  'sdd_enabled',
  'sdd_skip_research_simple',
  'sdd_research_prompt',
  'sdd_spec_prompt',
  'sdd_execute_prompt',
  'planner_system_prompt',
  'team_system_prompt',
  'hybrid_planner_prompt',
  'reviewer_prompt',
  'skeleton_system_prompt',
  'phase_detail_prompt',
  'estimate_system_prompt',
  'synthesis_prompt',
  'max_rounds',
  'max_messages',
  'timeout_min',
  'task_timeout_min',
  'max_retries',
  'convergence_interval',
];

// Status colors
const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  estimated: { bg: T.purpleBg, color: T.purple },
  generating: { bg: T.warningBg, color: T.warning },
  ready: { bg: T.primaryBg, color: T.primary },
  executing: { bg: T.warningBg, color: T.warning },
  completed: { bg: T.successBg, color: T.success },
  failed: { bg: T.danger, color: T.danger },
  error: { bg: T.danger, color: T.danger },
  cancelled: { bg: T.borderHover, color: T.textMuted },
};

// Inject pulse keyframe once
if (typeof document !== 'undefined' && !document.getElementById('admin-orch-style')) {
  const s = document.createElement('style');
  s.id = 'admin-orch-style';
  s.textContent = `@keyframes adminOrchPulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
.orch-brain-chip { display: inline-flex; align-items: center; gap: 3px; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 500; background: ${T.border}; border: 1px solid ${T.bgHover}; }`;
  document.head.appendChild(s);
}

export function AdminOrchestratorPage({ path }: { path?: string }) {
  const [tab, setTab] = useState<'config' | 'plans'>('plans');
  const [config, setConfig] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [editedFields, setEditedFields] = useState<Set<string>>(new Set());
  const [sddCategory, setSddCategory] = useState('programming');

  // Plans state
  const [plans, setPlans] = useState<any[]>([]);
  const [plansTotal, setPlansTotal] = useState(0);
  const [plansLoading, setPlansLoading] = useState(false);
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null);
  const [expandedPlanData, setExpandedPlanData] = useState<any>(null);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);

  if (!isAdmin.value) {
    return (
      <div style={{ padding: 32, color: T.textSecondary }}>
        <h2 style={{ color: T.text }}>Acesso restrito</h2>
        <p>Apenas administradores podem acessar esta página.</p>
      </div>
    );
  }

  useEffect(() => {
    if (tab === 'config') loadConfig();
    if (tab === 'plans') loadPlans();
  }, [tab]);

  // Auto-refresh plans list when there are generating plans
  const hasGeneratingRef = useRef(false);
  useEffect(() => {
    hasGeneratingRef.current = plans.some(p => p.status === 'generating');
  }, [plans]);

  useEffect(() => {
    if (tab !== 'plans') return;
    const interval = window.setInterval(() => {
      if (hasGeneratingRef.current) loadPlans(true);
    }, 8000);
    return () => clearInterval(interval);
  }, [tab]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const r: any = await api.get('/orchestrator/config');
      setConfig(r.config || {});
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key: string, value: string) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    setEditedFields(prev => new Set([...prev, key]));
    setSaved(false);
  };

  const saveConfig = async () => {
    setSaving(true);
    setError('');
    try {
      // Only send edited fields
      const updates: Record<string, string> = {};
      editedFields.forEach(key => {
        updates[key] = config[key] || '';
      });
      if (Object.keys(updates).length === 0) {
        setSaved(true);
        setSaving(false);
        return;
      }
      const r: any = await api.put('/orchestrator/config', { config: updates });
      setConfig(r.config || config);
      setEditedFields(new Set());
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setError(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const resetField = (key: string) => {
    editedFields.delete(key);
    setEditedFields(new Set(editedFields));
    loadConfig();
  };

  // Plans functions
  const loadPlans = async (silent = false) => {
    if (!silent) setPlansLoading(true);
    try {
      const r: any = await api.get('/orchestrator/admin/plans?limit=50');
      setPlans(r.plans || []);
      setPlansTotal(r.total || 0);
    } catch (e: any) {
      if (!silent) setError(e.message || 'Erro ao carregar planos');
    } finally {
      if (!silent) setPlansLoading(false);
    }
  };

  const expandPlan = async (planId: string) => {
    if (expandedPlanId === planId) {
      setExpandedPlanId(null);
      setExpandedPlanData(null);
      setExpandedTaskId(null);
      return;
    }
    setExpandedPlanId(planId);
    setExpandedPlanData(null);
    setExpandedTaskId(null);
    try {
      const r: any = await api.get(`/orchestrator/admin/plans/${planId}`);
      setExpandedPlanData(r);
    } catch {}
  };

  // Poll expanded plan if it's generating (live events)
  // Poll expanded plan ONLY if generating — stable interval, no flicker
  useEffect(() => {
    if (!expandedPlanId) return;
    const status = expandedPlanData?.plan?.status;
    if (status !== 'generating') return;
    const id = expandedPlanId;
    const interval = window.setInterval(async () => {
      try {
        const r: any = await api.get(`/orchestrator/admin/plans/${id}`);
        setExpandedPlanData(r);
        if (r.plan?.status !== 'generating') {
          loadPlans(true);
          clearInterval(interval);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [expandedPlanId, expandedPlanData?.plan?.status === 'generating']);

  const fmtDate = (iso: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  // Tab style
  const tabStyle = (active: boolean) => ({
    padding: '10px 24px', borderRadius: '8px 8px 0 0', border: 'none', cursor: 'pointer',
    fontSize: 14, fontWeight: 500, transition: 'all 0.2s',
    background: active ? T.bg : 'transparent',
    color: active ? T.text : T.textMuted,
    borderBottom: active ? '2px solid #7c5cfc' : '2px solid transparent',
  });

  return (
    <div style={{ padding: 32, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 600, color: T.text, margin: 0 }}>
            Orquestrador — Admin
          </h1>
          <p style={{ color: T.textMuted, fontSize: 14, marginTop: 4 }}>
            Planos de todos os usuários e configurações do sistema.
          </p>
        </div>
        {tab === 'config' && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {saved && <span style={{ color: T.success, fontSize: 13 }}>Salvo!</span>}
            {editedFields.size > 0 && (
              <span style={{ color: T.warning, fontSize: 12 }}>
                {editedFields.size} campo{editedFields.size > 1 ? 's' : ''} modificado{editedFields.size > 1 ? 's' : ''}
              </span>
            )}
            <Button
              variant={editedFields.size > 0 ? 'primary' : 'secondary'}
              onClick={saveConfig}
              disabled={saving || editedFields.size === 0}
              loading={saving}
            >
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: `1px solid ${T.border}` }}>
        <Button variant={tab === 'plans' ? 'primary' : 'ghost'} size="sm" onClick={() => setTab('plans')}>Planos ({plansTotal})</Button>
 <Button variant={tab === 'config' ? 'primary' : 'ghost'} size="sm" onClick={() => setTab('config')}>Configurações</Button>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', background: T.dangerBg, border: `1px solid ${T.dangerBg}`,
          borderRadius: 8, color: T.danger, fontSize: 14, marginBottom: 16,
        }}>
          {error} <Button variant="ghost" size="xs" onClick={() => setError('')} style={{ color: T.danger, marginLeft: 8 }}></Button>
        </div>
      )}

      {/* ═══ PLANS TAB ═══ */}
      {tab === 'plans' && (
        <div>
          {plansLoading ? (
            <div style={{ padding: 16 }}>{[1,2,3].map(i => (<div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.border}` }}><SkeletonBar width="25%" height={14} /><SkeletonBar width="35%" height={14} /><SkeletonBar width="20%" height={14} /></div>))}</div>
          ) : plans.length === 0 ? (
            <div style={{ color: T.textMuted, padding: 40, textAlign: 'center' }}>Nenhum plano criado ainda.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {plans.map((p: any) => {
                const isExpanded = expandedPlanId === p.id;
                const sc = STATUS_COLORS[p.status] ?? { bg: T.primaryBg, color: T.primary };
                const planJson = (() => { try { return JSON.parse(p.plan_json || '{}'); } catch { return {}; } })();
                return (
                  <div key={p.id}>
                    {/* Plan row */}
                    <div
                      onClick={() => expandPlan(p.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                        background: isExpanded ? T.bg : T.bgSubtle,
                        border: `1px solid ${isExpanded ? T.accent : T.bgCard}`,
                        borderRadius: isExpanded ? '12px 12px 0 0' : 12, cursor: 'pointer', transition: 'all 0.2s',
                      }}
                    >
                      <span style={{ color: T.textMuted, fontSize: 11, transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}></span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title || 'Sem título'}</div>
                        <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
 <span> {p.customer_email || p.customer_id?.substring(0, 8)}</span>
 <span> {fmtDate(p.created_at)}</span>
                          <span>{p.total_tasks} tasks</span>
 {p.mode && p.mode !== 'hub' && <span style={{ color: T.purple }}> {p.mode}</span>}
                          {planJson.complexity && <span style={{ color: planJson.complexity === 'complex' ? T.danger : planJson.complexity === 'medium' ? T.warning : T.success }}>{planJson.complexity}</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                        {p.status === 'executing' && <span style={{ fontSize: 12, color: T.textSecondary }}>{p.completed_tasks}/{p.total_tasks}</span>}
                        <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: sc.bg, color: sc.color }}>{p.status}</span>
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div style={{
                        background: T.bgSecondary, borderLeft: `1px solid ${T.accentBg}`,
                        borderRight: `1px solid ${T.accent}`, borderBottom: `1px solid ${T.accent}`,
                        borderRadius: '0 0 12px 12px', padding: 20,
                      }}>
                        {!expandedPlanData ? (
                          <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
                        ) : (
                          <div>
                            {/* Prompt */}
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ fontSize: 12, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Prompt Original</div>
                              <div style={{ fontSize: 13, color: T.textSecondary, background: T.blackAlpha30, padding: 12, borderRadius: 8, whiteSpace: 'pre-wrap', maxHeight: 150, overflow: 'auto' }}>
                                {expandedPlanData.plan?.original_prompt || '—'}
                              </div>
                            </div>

                            {/* Category + Brains + SDD Info */}
                            {(() => {
                              const plan = expandedPlanData.plan;
                              const cat = plan?.category;
                              const br = plan?.brains || [];
                              const catLabels: Record<string, string> = { programming: 'Programação', analysis: 'Análise', content: 'Conteúdo', generic: 'Geral' };
                              const catColors: Record<string, string> = { programming: T.purple, analysis: T.primary, content: '#4ade80', generic: T.textMuted };
                              // Extract SDD events for this plan
                              const sddEvents = (expandedPlanData.events || []).filter((e: any) => e.event_type === 'sdd_step');
                              const sddCategories = new Set(sddEvents.map((e: any) => {
                                try { return JSON.parse(e.event_data || '{}').category; } catch { return null; }
                              }).filter(Boolean));
                              return (
                                <div style={{ marginBottom: 16, background: T.accentBg, border: `1px solid ${T.accentBg}`, borderRadius: 10, padding: 14 }}>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Classificação</span>
                                    {cat && <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: `${catColors[cat] || T.textMuted}22`, color: catColors[cat] || T.textMuted }}>{catLabels[cat] || cat}</span>}
                                    <span style={{ fontSize: 11, color: T.textMuted }}>→ Prompts SDD: <b style={{ color: catColors[cat] || T.textMuted }}>{cat || 'generic'}</b></span>
                                  </div>
                                  {br.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
                                      <span style={{ fontSize: 11, color: T.textMuted, marginRight: 4 }}>Expertises detectadas:</span>
                                      {br.map((b: any) => (
                                        <span key={b.key} class="orch-brain-chip" style={{ color: b.color, borderColor: b.color + '33' }}>
                                          {b.emoji} {b.label}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  <div style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.6 }}>
                                    <div><b>Prompts utilizados ({cat || 'generic'}):</b></div>
                                    <div style={{ paddingLeft: 12, marginTop: 4 }}>
                                      <div>Research: <code style={{ color: T.purple, fontSize: 10 }}>sdd_{cat || 'generic'}_research_prompt</code></div>
                                      <div>Spec: <code style={{ color: T.primary, fontSize: 10 }}>sdd_{cat || 'generic'}_spec_prompt</code></div>
                                      <div>Execute: <code style={{ color: T.success, fontSize: 10 }}>sdd_{cat || 'generic'}_execute_prompt</code></div>
                                    </div>
                                  </div>
                                  {sddEvents.length > 0 && (
                                    <div style={{ marginTop: 10, fontSize: 11, color: T.textMuted }}>
                                      SDD Steps executados: <b style={{ color: T.text }}>{sddEvents.length}</b>
                                      {sddCategories.size > 0 && <span> • Categorias usadas: {Array.from(sddCategories).map((c: any) => catLabels[c] || c).join(', ')}</span>}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Generation log — always visible when events exist */}
                            {(() => {
                              const genEvents = (expandedPlanData.events || []).filter((e: any) => e.event_type === 'generation_step');
                              if (genEvents.length === 0 && expandedPlanData.plan?.status !== 'generating') return null;
                              const isGenerating = expandedPlanData.plan?.status === 'generating';
                              return (
                                <div style={{ marginBottom: 16 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                    {isGenerating ? (
                                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: T.warning, animation: 'adminOrchPulse 1s infinite' }} />
                                    ) : (
                                      <span style={{ fontSize: 14 }}></span>
                                    )}
                                    <span style={{ fontSize: 14, fontWeight: 600, color: isGenerating ? T.warning : T.textSecondary }}>
                                      {isGenerating ? 'Gerando plano em tempo real...' : `Log de geração (${genEvents.length} etapas)`}
                                    </span>
                                  </div>
                                  <div style={{ background: T.bgOverlay, borderRadius: 8, padding: 12, maxHeight: 300, overflow: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
                                    {genEvents.length === 0 && (
                                      <div style={{ color: T.textMuted, fontStyle: 'italic' }}>Aguardando primeiros eventos...</div>
                                    )}
                                    {genEvents.map((e: any, i: number) => {
                                      const data = (() => { try { return JSON.parse(e.event_data || '{}'); } catch { return {}; } })();
                                      const time = new Date(e.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                                      return (
                                        <div key={e.id || i} style={{ padding: '4px 0', borderBottom: `1px solid ${T.bgCard}`, color: data.step?.includes('done') || data.step === 'complete' ? T.success : data.step === 'error' ? T.danger : data.step?.includes('start') ? T.warning : T.textMuted }}>
                                          <span style={{ color: T.textMuted, marginRight: 8 }}>{time}</span>
                                          {data.message || e.event_type}
                                          {data.phases && <span style={{ color: T.accent, marginLeft: 8 }}>({data.phases.join(', ')})</span>}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Estimate-specific view */}
                            {expandedPlanData.plan?.status === 'estimated' && (() => {
                              const est = (() => { try { return JSON.parse(expandedPlanData.plan?.plan_json || '{}'); } catch { return {}; } })();
                              return (
                                <div style={{ marginBottom: 16 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                    <span style={{ fontSize: 16 }}></span>
                                    <span style={{ fontSize: 14, fontWeight: 600, color: T.purple }}>Estimativa (plano não criado)</span>
                                    <span style={{
                                      padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                                      background: est.complexity === 'complex' ? T.dangerBg : est.complexity === 'simple' ? T.successBg : T.warningBg,
                                      color: est.complexity === 'complex' ? T.danger : est.complexity === 'simple' ? T.success : T.warning,
                                    }}>
                                      {est.complexity === 'simple' ? 'Simples' : est.complexity === 'complex' ? 'Complexo' : 'Médio'}
                                    </span>
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
                                    {[
                                      { label: 'Tasks Est.', value: est.estimated_tasks || '?', color: T.accent },
                                      { label: 'Fases', value: est.estimated_phases || '?', color: T.purple },
                                      { label: 'Custo USD', value: `$${(est.estimated_cost_usd || 0).toFixed(2)}`, color: T.success },
                                      { label: 'Tempo Est.', value: est.estimated_total_min ? `${est.estimated_total_min}min` : '?', color: T.warning },
                                    ].map((s, i) => (
                                      <div key={i} style={{ textAlign: 'center', padding: '10px 0', background: T.bgOverlay, borderRadius: 8 }}>
                                        <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
                                        <div style={{ fontSize: 11, color: T.textMuted }}>{s.label}</div>
                                      </div>
                                    ))}
                                  </div>
                                  {est.phases_preview && est.phases_preview.length > 0 && (
                                    <div style={{ marginBottom: 12 }}>
                                      <div style={{ fontSize: 12, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Fases Previstas</div>
                                      {est.phases_preview.map((ph: any, i: number) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: T.accentBg, borderLeft: `3px solid ${T.accentBg}`, borderRadius: '0 6px 6px 0', marginBottom: 4 }}>
                                          <span style={{ fontSize: 11, color: T.accent, fontWeight: 600 }}>{i + 1}</span>
                                          <span style={{ fontSize: 13, color: T.text, flex: 1 }}>{ph.name}</span>
                                          <span style={{ fontSize: 11, color: T.textSecondary }}>{ph.tasks_count} tasks • ~{ph.estimated_min}min</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
 {est.summary && <div style={{ fontSize: 12, color: T.textSecondary, fontStyle: 'italic', marginBottom: 8 }}> {est.summary}</div>}
                                  {est.model && <div style={{ fontSize: 11, color: T.textMuted }}>Modelo: {est.model}</div>}
                                </div>
                              );
                            })()}

                            {/* Stats (non-estimate) */}
                            {expandedPlanData.plan?.status !== 'estimated' && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16 }}>
                              {[
                                { label: 'Tasks', value: expandedPlanData.plan?.total_tasks || 0, color: T.accent },
                                { label: 'Completas', value: expandedPlanData.plan?.completed_tasks || 0, color: T.success },
                                { label: 'Falhas', value: expandedPlanData.plan?.failed_tasks || 0, color: T.danger },
                                { label: 'Est. (min)', value: expandedPlanData.plan?.estimated_duration_min || '—', color: T.warning },
                                { label: 'Real (min)', value: expandedPlanData.plan?.actual_duration_min || '—', color: T.primary },
                              ].map((s, i) => (
                                <div key={i} style={{ textAlign: 'center', padding: '8px 0', background: T.bgOverlay, borderRadius: 8 }}>
                                  <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                                  <div style={{ fontSize: 11, color: T.textMuted }}>{s.label}</div>
                                </div>
                              ))}
                            </div>
                            )}

                            {/* Tasks */}
                            {(expandedPlanData.tasks || []).length > 0 && <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 8, fontWeight: 500 }}>Tasks ({(expandedPlanData.tasks || []).length})</div>}
                            {(expandedPlanData.tasks || []).map((t: any, i: number) => {
                              const tsc = STATUS_COLORS[t.status] ?? { bg: T.primaryBg, color: T.primary };
                              const isTaskExpanded = expandedTaskId === t.id;
                              const execStep = t.execution_step || 'pending';
                              const sddSteps = [
                                { key: 'research', icon: '', label: 'Research', doneSteps: ['researched', 'speccing', 'specced', 'executing', 'executed', 'completed'] },
                                { key: 'spec', icon: '', label: 'Spec', doneSteps: ['specced', 'executing', 'executed', 'completed'] },
                                { key: 'execute', icon: '', label: 'Execute', doneSteps: ['executed', 'completed'] },
                                { key: 'done', icon: '', label: 'Done', doneSteps: ['completed'] },
                              ];
                              return (
                                <div key={t.id} style={{ marginBottom: 4 }}>
                                  <div
                                    onClick={() => setExpandedTaskId(isTaskExpanded ? null : t.id)}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                                      background: T.bgCard, borderLeft: `3px solid ${tsc.color}`,
                                      borderRadius: '0 6px 6px 0', cursor: 'pointer', transition: 'all 0.2s',
                                    }}
                                  >
                                    <span style={{ fontSize: 11, color: T.textMuted, minWidth: 20 }}>{i + 1}</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: 13, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                                      <div style={{ fontSize: 11, color: T.textMuted, display: 'flex', gap: 8, marginTop: 2 }}>
                                        <span>{t.task_type}</span>
                                        {t.phase && <span style={{ color: T.accent }}>{t.phase}</span>}
                                        {t.assigned_agent_name && <span>{t.assigned_agent_name}</span>}
                                        {t.estimated_duration_min && <span>~{t.estimated_duration_min}min</span>}
                                      </div>
                                    </div>
                                    {/* SDD progress mini */}
                                    {execStep !== 'pending' && (
                                      <div style={{ display: 'flex', gap: 2, marginRight: 6 }}>
                                        {sddSteps.map(s => (
                                          <span key={s.key} style={{ fontSize: 10, opacity: s.doneSteps.includes(execStep) ? 1 : 0.25 }}>{s.icon}</span>
                                        ))}
                                      </div>
                                    )}
                                    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: tsc.bg, color: tsc.color }}>{execStep !== 'pending' ? execStep : t.status}</span>
                                  </div>
                                  {isTaskExpanded && (
                                    <div style={{ padding: '10px 12px 10px 45px', background: T.bgOverlay, borderRadius: '0 0 6px 0', fontSize: 12 }}>
                                      {/* SDD Progress Bar */}
                                      {execStep !== 'pending' && (
                                        <div style={{ display: 'flex', gap: 8, marginBottom: 12, padding: '8px 12px', background: T.accentBg, borderRadius: 8 }}>
                                          {sddSteps.map((s, si) => {
                                            const active = s.doneSteps.includes(execStep);
                                            const current = (execStep === 'researching' && s.key === 'research') || (execStep === 'speccing' && s.key === 'spec') || (execStep === 'executing' && s.key === 'execute');
                                            return (
                                              <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <span style={{ fontSize: 16, filter: active ? 'none' : 'grayscale(1)', opacity: active ? 1 : 0.3, animation: current ? 'adminOrchPulse 1s infinite' : 'none' }}>{s.icon}</span>
                                                <span style={{ fontSize: 11, color: active ? T.text : T.textMuted, fontWeight: current ? 600 : 400 }}>{s.label}</span>
                                                {si < sddSteps.length - 1 && <span style={{ color: T.textPrimary, margin: '0 4px' }}>→</span>}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                      {t.description && <div style={{ color: T.textSecondary, marginBottom: 8, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{t.description}</div>}
                                      {/* SDD Sections */}
                                      {t.research_output && (
                                        <div style={{ marginBottom: 8 }}>
                                          <div style={{ color: T.purple, textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.5px', marginBottom: 4 }}>Research Output</div>
                                          <div style={{ color: T.textSecondary, background: T.blackAlpha30, padding: 10, borderRadius: 6, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>{t.research_output}</div>
                                        </div>
                                      )}
                                      {t.spec_output && (
                                        <div style={{ marginBottom: 8 }}>
                                          <div style={{ color: T.primary, textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.5px', marginBottom: 4 }}>Spec Output</div>
                                          <div style={{ color: T.textSecondary, background: T.blackAlpha30, padding: 10, borderRadius: 6, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>{t.spec_output}</div>
                                        </div>
                                      )}
                                      {t.output && (
                                        <div>
                                          <div style={{ color: T.success, textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.5px', marginBottom: 4 }}>Resultado Final</div>
                                          <div style={{ color: T.textSecondary, background: T.blackAlpha30, padding: 10, borderRadius: 6, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>{t.output}</div>
                                        </div>
                                      )}
                                      {t.review_notes && <div style={{ color: T.textSecondary, marginTop: 6 }}>Review: {t.review_notes}</div>}
                                      {t.output_quality && <div style={{ color: T.textSecondary, marginTop: 4 }}>Qualidade: {t.output_quality}</div>}
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                            {/* Final output */}
                            {expandedPlanData.plan?.final_output && (
                              <div style={{ marginTop: 16 }}>
                                <div style={{ fontSize: 12, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Resultado Final</div>
                                <div style={{ fontSize: 13, color: T.text, background: T.blackAlpha30, padding: 14, borderRadius: 8, whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>
                                  {expandedPlanData.plan.final_output}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ CONFIG TAB ═══ */}
      {tab === 'config' && (loading ? (
        <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* ── SDD Category Prompts Section ── */}
          <div style={{
            background: T.bgSecondary, border: `1px solid ${T.accentBg}`,
            borderRadius: 12, padding: 20,
          }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 4 }}>
                SDD Prompts por Categoria
              </div>
              <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
                Prompts especializados por tipo de tarefa. A categoria é detectada automaticamente via keywords.
                Se o prompt da categoria estiver vazio ou "USE_CODE_DEFAULT", usa o default hardcoded.
              </div>
            </div>

            {/* Category tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
              {SDD_CATEGORIES.map(cat => (
                <Button
                  key={cat.id}
                  variant={sddCategory === cat.id ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setSddCategory(cat.id)}
                >
                  {cat.label}
                </Button>
              ))}
            </div>

            {/* Prompts for selected category */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {(SDD_CATEGORY_FIELDS[sddCategory] || []).map(key => {
                const meta = FIELD_META[key];
                if (!meta) return null;
                const isEdited = editedFields.has(key);
                const stepLabel = key.includes('_research_') ? 'Research' : key.includes('_spec_') ? 'Spec' : 'Execute';
                return (
                  <div key={key} style={{
                    background: T.bgOverlay, border: `1px solid ${isEdited ? T.accentBg : T.bgCard}`,
                    borderRadius: 8, padding: 16,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: T.text }}>{stepLabel}</div>
                        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{meta.description}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button variant="ghost" size="xs" onClick={() => updateField(key, 'USE_CODE_DEFAULT')} style={{ color: T.warning, border: `1px solid ${T.warningBgStrong}` }}>
 Default
                        </Button>
                        {isEdited && (
                          <Button variant="ghost" size="xs" onClick={() => resetField(key)}>
                            Resetar
                          </Button>
                        )}
                      </div>
                    </div>
                    <Textarea
                      value={config[key] === 'USE_CODE_DEFAULT' ? '' : (config[key] || '')}
                      onInput={(e: any) => updateField(key, e.target.value || 'USE_CODE_DEFAULT')}
                      placeholder="Vazio = usa o prompt padrão do código automaticamente"
                      style={{
                        width: '100%', minHeight: 160, maxHeight: 400, background: T.bgSecondary,
                        border: `1px solid ${T.border}`, borderRadius: 6,
                        color: T.text, fontSize: 12, padding: 12, resize: 'vertical',
                        fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.5,
                        outline: 'none',
                      }}
                      onFocus={(e: any) => { e.target.style.borderColor = T.accent; }}
                      onBlur={(e: any) => { e.target.style.borderColor = T.border; }}
                    />
                    <div style={{ fontSize: 10, color: T.textDisabled, marginTop: 4 }}>
                      {config[key] === 'USE_CODE_DEFAULT' || !config[key]
                        ? <span style={{ color: T.success }}>Usando prompt padrão do código</span>
                        : <span>{(config[key] || '').length} caracteres (customizado)</span>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {FIELD_ORDER.map(key => {
            const meta = FIELD_META[key];
            if (!meta) return null;
            const isEdited = editedFields.has(key);
            return (
              <div
                key={key}
                style={{
                  background: T.bgSecondary, border: `1px solid ${isEdited ? T.accentBg : T.border}`,
                  borderRadius: 12, padding: 20, transition: 'border-color 0.3s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 500, color: T.text }}>{meta.label}</div>
                    <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4, lineHeight: 1.5 }}>{meta.description}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {meta.type === 'textarea' && (
                      <Button variant="ghost" size="xs" onClick={() => updateField(key, 'USE_CODE_DEFAULT')} title="Restaura o prompt para o valor padrão do código" style={{ color: T.warning, border: `1px solid ${T.warningBgStrong}` }}>
 Restaurar Padrão
                      </Button>
                    )}
                    {isEdited && (
                      <Button variant="ghost" size="xs" onClick={() => resetField(key)}>
                        Resetar
                      </Button>
                    )}
                  </div>
                </div>
                {meta.type === 'boolean' ? (
                  <div
                    onClick={() => updateField(key, config[key] === 'true' ? 'false' : 'true')}
                    style={{
                      width: 48, height: 26, borderRadius: 13, cursor: 'pointer',
                      background: config[key] === 'true' ? T.accent : T.bgActive,
                      position: 'relative', transition: 'background 0.2s',
                    }}
                  >
                    <div style={{
                      width: 20, height: 20, borderRadius: '50%', background: '#fff',
                      position: 'absolute', top: 3,
                      left: config[key] === 'true' ? 25 : 3,
                      transition: 'left 0.2s',
                    }} />
                  </div>
                ) : meta.type === 'textarea' ? (
                  <Textarea
                    value={config[key] || ''}
                    onInput={(e: any) => updateField(key, e.target.value)}
                    style={{
                      width: '100%', minHeight: 200, maxHeight: 500, background: T.bgSecondary,
                      border: `1px solid ${T.borderHover}`, borderRadius: 8,
                      color: T.text, fontSize: 13, padding: 14, resize: 'vertical',
                      fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.6,
                      outline: 'none', transition: 'border-color 0.3s',
                    }}
                    onFocus={(e: any) => { e.target.style.borderColor = T.accent; }}
                    onBlur={(e: any) => { e.target.style.borderColor = T.borderHover; }}
                  />
                ) : (
                  <input
                    type="number"
                    value={config[key] || ''}
                    onInput={(e: any) => updateField(key, e.target.value)}
                    style={{
                      width: 120, height: 40, background: T.bgSecondary,
                      border: `1px solid ${T.borderHover}`, borderRadius: 8,
                      color: T.text, fontSize: 15, padding: '0 14px',
                      outline: 'none', transition: 'border-color 0.3s',
                    }}
                    onFocus={(e: any) => { e.target.style.borderColor = T.accent; }}
                    onBlur={(e: any) => { e.target.style.borderColor = T.borderHover; }}
                  />
                )}
                {meta.type === 'textarea' && (
                  <div style={{ fontSize: 11, color: T.textDisabled, marginTop: 6 }}>
                    {(config[key] || '').length} caracteres
                  </div>
                )}
              </div>
            );
          })}

          {/* ── Detection Info Section ── */}
          <div style={{
            background: T.bgSecondary, border: `1px solid ${T.accentBg}`,
            borderRadius: 12, padding: 20, marginTop: 12,
          }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 4 }}>
              Detecção Automática (Categorias + Cérebros)
            </div>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16, lineHeight: 1.5 }}>
              A detecção primária é feita pelo LLM durante a estimativa (funciona em TODAS as línguas).
              Keywords são usadas como fallback. Categorias determinam qual conjunto de prompts SDD será usado.
            </div>

            {/* Categories */}
            <div style={{ marginBottom: 16 }}>
 <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Categorias Disponíveis</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { key: 'programming', emoji: '', label: 'Programação', color: T.purple, desc: 'Apps, sites, APIs, bots, bugs, desenvolvimento' },
                  { key: 'analysis', emoji: '', label: 'Análise', color: T.primary, desc: 'SEO, mercado, dados, relatórios, concorrência' },
                  { key: 'content', emoji: '', label: 'Conteúdo', color: '#4ade80', desc: 'Textos, artigos, blogs, emails, copywriting' },
                  { key: 'generic', emoji: '', label: 'Geral', color: T.textMuted, desc: 'Fallback para tudo que não encaixa acima' },
                ].map(c => (
                  <div key={c.key} style={{ background: `${c.color}11`, border: `1px solid ${c.color}33`, borderRadius: 8, padding: '8px 12px', minWidth: 180 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: c.color }}>{c.emoji} {c.label}</div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{c.desc}</div>
                    <div style={{ fontSize: 10, color: T.textDisabled, marginTop: 4, fontFamily: 'monospace' }}>→ sdd_{c.key}_*_prompt</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Brains */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Cérebros/Expertises Disponíveis</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[
 { key: 'mobile', emoji: '', label: 'Mobile Dev', color: T.success },
 { key: 'frontend', emoji: '', label: 'Frontend', color: T.primary },
 { key: 'backend', emoji: '', label: 'Backend', color: T.purple },
                  { key: 'devops', emoji: '', label: 'DevOps', color: T.warning },
 { key: 'designer', emoji: '', label: 'Designer', color: T.danger },
                  { key: 'architect', emoji: '', label: 'Architect', color: T.purple },
 { key: 'security', emoji: '', label: 'Security', color: T.danger },
 { key: 'seo', emoji: '', label: 'SEO', color: T.success },
 { key: 'marketing', emoji: '', label: 'Marketing', color: T.warning },
                  { key: 'data_analyst', emoji: '', label: 'Data Analyst', color: '#06b6d4' },
 { key: 'influencer', emoji: '', label: 'Influencer', color: '#e879f9' },
                  { key: 'copywriter', emoji: '', label: 'Copywriter', color: T.success },
 { key: 'qa_tester', emoji: '', label: 'QA Tester', color: '#84cc16' },
                  { key: 'product_manager', emoji: '', label: 'Product Manager', color: T.warning },
                ].map(b => (
                  <span key={b.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 10, fontSize: 11, fontWeight: 500, background: `${b.color}15`, border: `1px solid ${b.color}33`, color: b.color }}>
                    {b.emoji} {b.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Test detection */}
            <div>
 <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Testar Detecção</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  placeholder="Cole um prompt para testar a detecção..."
                  id="detection-test-input"
                  style={{ flex: 1, background: T.bgSecondary, border: `1px solid ${T.borderHover}`, borderRadius: 8, color: T.text, padding: '8px 12px', fontSize: 13, outline: 'none' }}
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={async () => {
                    const input = (document.getElementById('detection-test-input') as HTMLInputElement)?.value;
                    if (!input) return;
                    try {
                      const r: any = await api.post('/orchestrator/admin/test-detection', { prompt: input });
                      const el = document.getElementById('detection-test-result');
                      if (el) {
                        const brainChips = (r.brains || []).map((b: any) => `${b.emoji} ${b.label}`).join(', ');
                        el.innerHTML = `<span style="color:#a78bfa">Categoria: <b>${r.category}</b></span> &nbsp;|&nbsp; <span style="color:#60a5fa">Cérebros: <b>${brainChips || 'nenhum'}</b></span><br/><span style="color:#555;font-size:10px">${r.note}</span>`;
                      }
                    } catch (e: any) {
                      const el = document.getElementById('detection-test-result');
                      if (el) el.innerHTML = `<span style="color:${T.danger}">Erro: ${e.message}</span>`;
                    }
                  }}
                >
                  Testar
                </Button>
              </div>
              <div id="detection-test-result" style={{ marginTop: 8, fontSize: 12, color: T.textSecondary, minHeight: 20 }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
